/**
 * US phone helpers shared by the signup/intake flows. Cognito stores the number
 * in the `custom:phone` attribute and only accepts E.164, so we normalize to
 * `+1NNNNNNNNNNN` and validate before submit.
 */

/**
 * Coerce a phone string toward E.164 (+1 format). We assume US numbers only and
 * normalize 10-digit or 11-digit (1+10) formats to `+1NNNNNNNNNNN`.
 */
export function normalizePhone(input: string): string {
  const digits = input.replace(/[^0-9]/g, "");
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  // Fallback; will likely fail Cognito validation.
  return `+1${digits}`;
}

// US phone number: permits formatting chars and requires 10-11 digits total.
const PHONE_RE = /^[\d\s().-]{10,}$/;

function digitCount(s: string): number {
  let n = 0;
  for (const ch of s) if (ch >= "0" && ch <= "9") n++;
  return n;
}

/**
 * Validate a US phone string for sign-up. Returns a user-facing error message,
 * or `null` if the input is acceptable.
 */
export function validatePhone(input: string): string | null {
  const trimmed = input.trim();
  if (!trimmed) return "Phone number is required.";
  if (!PHONE_RE.test(trimmed)) {
    return "Enter a phone number like (312) 555-1212.";
  }
  const digits = digitCount(trimmed);
  if (digits === 10) return null;
  if (digits === 11 && trimmed.replace(/[^0-9]/g, "").startsWith("1")) return null;
  if (digits < 10) {
    return "That's too short — US numbers need 10 digits, like (312) 555-1212.";
  }
  return "US phone numbers have 10 digits, like (312) 555-1212.";
}
